'use client';

import * as React from 'react';
import * as SliderPrimitive from '@radix-ui/react-slider';

import { cn } from '../lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

export interface SliderProps
  extends React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root> {
  /**
   * Whether to show a tooltip on thumb hover/drag
   */
  showTooltip?: boolean;
  /**
   * Format function for tooltip display
   */
  formatTooltip?: (value: number) => string;
}

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      defaultValue,
      value,
      min = 0,
      max = 100,
      showTooltip = false,
      formatTooltip = (val) => String(val),
      onValueChange,
      ...props
    },
    ref,
  ) => {
    // Compute initial value: use provided value/defaultValue, or single thumb at min
    const initialValue = React.useMemo(
      () =>
        Array.isArray(value)
          ? value
          : Array.isArray(defaultValue)
            ? defaultValue
            : [min],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );

    // Track internal state for uncontrolled mode to show live tooltip values
    const [internalValue, setInternalValue] =
      React.useState<number[]>(initialValue);

    // Use controlled value if provided, otherwise use internal state
    const _values = Array.isArray(value) ? value : internalValue;

    const handleValueChange = (newValue: number[]) => {
      setInternalValue(newValue);
      onValueChange?.(newValue);
    };

    const [openTooltips, setOpenTooltips] = React.useState<boolean[]>(
      Array(_values.length).fill(false),
    );

    // Sync openTooltips array length when _values.length changes
    React.useEffect(() => {
      setOpenTooltips((prev) => {
        if (prev.length === _values.length) return prev;
        if (_values.length < prev.length) {
          return prev.slice(0, _values.length);
        }
        return [...prev, ...Array(_values.length - prev.length).fill(false)];
      });
    }, [_values.length]);

    const handleTooltipChange = (index: number, open: boolean) => {
      setOpenTooltips((prev) => {
        const next = [...prev];
        next[index] = open;
        return next;
      });
    };

    const thumbs = Array.from({ length: _values.length }, (_, index) => {
      const thumb = (
        <SliderPrimitive.Thumb
          key={index}
          className="block h-4 w-4 rounded-full border border-gray-300 bg-white shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:border-primary cursor-pointer"
        />
      );

      if (!showTooltip) return thumb;

      return (
        <Tooltip
          key={index}
          open={openTooltips[index]}
          onOpenChange={(open) => handleTooltipChange(index, open)}
        >
          <TooltipTrigger asChild>
            <SliderPrimitive.Thumb
              className="block h-4 w-4 rounded-full border border-gray-300 bg-white shadow transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 hover:border-primary cursor-pointer"
              onPointerDown={() => handleTooltipChange(index, true)}
              onPointerUp={() => handleTooltipChange(index, false)}
            />
          </TooltipTrigger>
          <TooltipContent side="top" className="px-2 py-1 text-xs">
            {formatTooltip(_values[index])}
          </TooltipContent>
        </Tooltip>
      );
    });

    const slider = (
      <SliderPrimitive.Root
        ref={ref}
        min={min}
        max={max}
        defaultValue={defaultValue}
        value={value}
        onValueChange={handleValueChange}
        className={cn(
          'relative flex w-full touch-none select-none items-center',
          className,
        )}
        {...props}
      >
        <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-gray-200">
          <SliderPrimitive.Range className="absolute h-full bg-primary" />
        </SliderPrimitive.Track>
        {thumbs}
      </SliderPrimitive.Root>
    );

    if (showTooltip) {
      return <TooltipProvider>{slider}</TooltipProvider>;
    }

    return slider;
  },
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
